import { useEffect, useState } from "react";
import API_BASE from "../config";

/**
 * Feature flag for the optional Payroll/Timesheet/Calendar module.
 *
 * The school system ships with the module bolted on but hidden by default.
 * A SuperAdmin enables it from Settings → Maintenance ("Enable Payroll
 * module"); the value is persisted as a `payroll.enabled` key on the
 * shared `/api/site-content` map. Any component that wants to render
 * payroll UI calls usePayrollEnabled() and gates on the boolean.
 *
 * Same hot-cache pattern as utils/companyDefaults.js — one fetch shared
 * across the app, every save broadcasts to mounted hooks so the topbar /
 * routes update without a reload.
 */

const SITE_CONTENT_KEY = "payroll.enabled";

let cache = false;          // resolved boolean
let hydrated = false;       // true once the first /api/site-content read returned
let inflight = null;        // in-flight Promise so concurrent callers share the same fetch
const listeners = new Set();

function notify() {
  for (const fn of listeners) fn(cache);
}

/** Fetch `/api/site-content`, read the payroll.enabled flag, update the
 *  cache and notify subscribers. Idempotent — multiple in-flight callers
 *  share the same Promise. */
export async function fetchPayrollEnabled(force = false) {
  if (!force && hydrated) return cache;
  if (inflight) return inflight;
  inflight = fetch(`${API_BASE}/api/site-content`)
    .then(r => r.ok ? r.json() : {})
    .then(map => {
      const raw = (map && map[SITE_CONTENT_KEY]) || "";
      cache = String(raw).toLowerCase() === "true";
      hydrated = true;
      notify();
      return cache;
    })
    .catch(() => {
      // Network error — leave cache as the last-known value so the UI
      // doesn't suddenly hide everything on a hiccup. hydrated stays
      // false so the next call retries.
      return cache;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

/** Optimistic local update — call after a successful PUT to
 *  /api/site-content so the topbar / routes flip immediately without
 *  waiting for the next /api/site-content fetch. */
export function setPayrollEnabledLocally(next) {
  cache = !!next;
  hydrated = true;
  notify();
}

/** React hook. Returns the current boolean and triggers a fetch on first
 *  mount if the cache is empty. */
export function usePayrollEnabled() {
  const [enabled, setEnabled] = useState(cache);
  useEffect(() => {
    let mounted = true;
    listeners.add(setEnabled);
    if (!hydrated) {
      fetchPayrollEnabled().then(v => { if (mounted) setEnabled(v); });
    }
    return () => {
      mounted = false;
      listeners.delete(setEnabled);
    };
  }, []);
  return enabled;
}
